/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { MealSettings } from '../models/MealSettings';
import type { Message } from '../models/Message';
import type { PaginatedMessageList } from '../models/PaginatedMessageList';
import type { PaginatedRecentMessagePatientsList } from '../models/PaginatedRecentMessagePatientsList';
import type { PatchedMessage } from '../models/PatchedMessage';
import type { SendMessage } from '../models/SendMessage';
import type { StationBoardToggle } from '../models/StationBoardToggle';
import type { StationBoardToggleResponse } from '../models/StationBoardToggleResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class PractitionersService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * @returns MealSettings
     * @throws ApiError
     */
    public practitionersMealsSettingsCreate({
        requestBody,
    }: {
        requestBody: MealSettings,
    }): CancelablePromise<MealSettings> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/practitioners/meals/settings/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns PaginatedMessageList
     * @throws ApiError
     */
    public practitionersMessagesList({
        group,
        limit,
        offset,
        ordering,
        patient,
        search,
        sentByMe,
    }: {
        /**
         * Group by patient
         */
        group?: boolean,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
        /**
         * Which field to use when ordering the results.
         */
        ordering?: string,
        patient?: number,
        /**
         * A search term.
         */
        search?: string,
        /**
         * Sent by me
         */
        sentByMe?: boolean,
    }): CancelablePromise<PaginatedMessageList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/practitioners/messages/',
            query: {
                'group': group,
                'limit': limit,
                'offset': offset,
                'ordering': ordering,
                'patient': patient,
                'search': search,
                'sent_by_me': sentByMe,
            },
        });
    }

    /**
     * @returns Message
     * @throws ApiError
     */
    public practitionersMessagesRetrieve({
        id,
    }: {
        id: number,
    }): CancelablePromise<Message> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/practitioners/messages/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns Message
     * @throws ApiError
     */
    public practitionersMessagesUpdate({
        id,
        requestBody,
    }: {
        id: number,
        requestBody: Message,
    }): CancelablePromise<Message> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/api/practitioners/messages/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns Message
     * @throws ApiError
     */
    public practitionersMessagesPartialUpdate({
        id,
        requestBody,
    }: {
        id: number,
        requestBody?: PatchedMessage,
    }): CancelablePromise<Message> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/api/practitioners/messages/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns void
     * @throws ApiError
     */
    public practitionersMessagesDestroy({
        id,
    }: {
        id: number,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/api/practitioners/messages/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Resend notification for a specific message to the patient
     * @returns any Notification resent successfully
     * @throws ApiError
     */
    public resendMessageNotification({
        id,
    }: {
        /**
         * Message ID
         */
        id: number,
    }): CancelablePromise<{
        status?: string;
    }> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/practitioners/messages/{id}/resend-notification/',
            path: {
                'id': id,
            },
            errors: {
                403: `Not a practitioner`,
                404: `Message not found`,
            },
        });
    }

    /**
     * @returns PaginatedRecentMessagePatientsList
     * @throws ApiError
     */
    public practitionersMessagesRecentPatientsList({
        limit,
        offset,
    }: {
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedRecentMessagePatientsList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/practitioners/messages/recent-patients/',
            query: {
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * @returns SendMessage
     * @throws ApiError
     */
    public practitionersMessagesSendCreate({
        requestBody,
    }: {
        requestBody: SendMessage,
    }): CancelablePromise<SendMessage> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/practitioners/messages/send/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Resend onboarding email to a patient. Limited to one email per patient per minute.
     * @returns any Email resent successfully
     * @throws ApiError
     */
    public resendPatientOnboardingEmail({
        patientId,
    }: {
        /**
         * Patient ID of the patient
         */
        patientId: number,
    }): CancelablePromise<{
        status?: string;
    }> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/practitioners/patients/{patient_id}/resend-onboarding-email/',
            path: {
                'patient_id': patientId,
            },
            errors: {
                400: `Invalid profile ID or patient does not have an email`,
                403: `Not a practitioner or rate limit exceeded`,
                404: `Patient profile not found`,
            },
        });
    }

    /**
     * Toggle StationBoard mode
     * Enable or disable StationBoard mode for practitioner
     * @returns StationBoardToggleResponse Successfully enabled StationBoard mode
     * @throws ApiError
     */
    public practitionersStationboardToggleCreate({
        requestBody,
    }: {
        requestBody: StationBoardToggle,
    }): CancelablePromise<StationBoardToggleResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/practitioners/stationboard/toggle/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

}
